import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import type { ReactNode } from "react";
import { cx } from "../../lib/cx";
import { LayerOwned, useLayerRoot } from "./presence";
import { PUSH_SURFACE } from "./stack";
import { useCoverUnderlay } from "./underlay";

type InitialFocus = BaseDialog.Popup.Props["initialFocus"];

export interface FullScreenLayerProps {
  children?: ReactNode;
  /**
   * Where focus lands on open. A function receives the interaction type, so
   * a keyboard opening can land on a button while a tap lands on a scroller
   * and draws no focus ring nobody asked for. Base UI's default otherwise.
   */
  initialFocus?: InitialFocus;
  /** The accessible name. The layer draws no heading of its own. */
  label: string;
  /** Bind `false` to the router's `closeLayer()`: Escape and Back call it. */
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

/**
 * A screen-sized layer over the app — today only the swap picker. Controlled,
 * and knows nothing of the router.
 *
 * `trap-focus`, not a full modal: Tab cycles inside and focus returns to the
 * trigger on close, but the toast lives outside this popup and a modal would
 * block the pointer on its "Desfazer". Pointer dismissal is off for the same
 * reason — the toast is the only thing outside a full-screen layer, and
 * pressing it is not a request to leave.
 *
 * z-40: above the tab bar (z-30), below the dialogs and sheets (z-50) a
 * layer opens over itself. Clear of the docked trainer on desktop.
 */
export function FullScreenLayer({
  open,
  onOpenChange,
  label,
  initialFocus,
  children,
}: FullScreenLayerProps) {
  // `D193`: pushed in on the shared axis, and kept mounted by the shell while
  // it slides back out.
  const layer = useLayerRoot(open);
  useCoverUnderlay(layer.open);
  return (
    <BaseDialog.Root
      disablePointerDismissal
      modal="trap-focus"
      onOpenChange={onOpenChange}
      onOpenChangeComplete={layer.onOpenChangeComplete}
      open={layer.open}
    >
      <BaseDialog.Portal>
        <BaseDialog.Popup
          aria-label={label}
          className={cx(
            "fixed inset-0 z-40 flex flex-col bg-canvas pt-safe font-ui text-ink outline-none lg:right-(--coach-dock,0px)",
            PUSH_SURFACE
          )}
          data-surface="push"
          initialFocus={initialFocus}
          {...layer.popupProps}
        >
          <LayerOwned>{children}</LayerOwned>
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}
